import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Search, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import { debounce } from '@/lib/utils';
import { useLazyGetSearchAutoCompleteQuery } from '@/redux/post/post.api.slice';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import {
  feedSearchPath,
  pickCommitLabel,
  resolveSearchTarget,
  type SearchMode,
  type SuggestionPost,
} from './searchTarget';

// Placeholder shown per selected mode.
const MODE_PLACEHOLDER: Record<SearchMode, string> = {
  title: 'Search projects by title',
  user: 'Search projects by username',
};

// Minimum characters before fetching autocomplete suggestions.
const MIN_SUGGEST_CHARS = 2;

const MODES: SearchMode[] = ['title', 'user'];
const MODE_LABEL: Record<SearchMode, string> = {
  title: 'Posts',
  user: 'Users',
};

function FeedSearchInput({ className }: { className?: string }) {
  const { getSearchParam, appendMultipleSearchParams, removeAllSearchParams } =
    useSearchParamActions();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector(selectCurrentUser);

  // Off the feed there is nothing to filter, so live URL writes are suppressed
  // and the bar commits by navigating instead.
  const onFeed = useLocation().pathname === '/';
  const onFeedRef = useRef(onFeed);
  useEffect(() => {
    onFeedRef.current = onFeed;
  });

  const urlSearchQuery = getSearchParam('searchQuery');
  const urlSearchMode = getSearchParam('searchMode');
  const urlValue = urlSearchQuery ?? '';
  const initialMode: SearchMode = urlSearchMode === 'user' ? 'user' : 'title';

  const [inputValue, setInputValue] = useState(urlValue);
  const [mode, setMode] = useState<SearchMode>(initialMode);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);

  // The hook's URL-writer callbacks are recreated on every URL change, so a
  // debounced closure that captured them once would write a STALE params
  // snapshot and clobber sibling filters. Keep a ref to the latest callbacks.
  const actionsRef = useRef({
    appendMultipleSearchParams,
    removeAllSearchParams,
  });
  useEffect(() => {
    actionsRef.current = { appendMultipleSearchParams, removeAllSearchParams };
  });

  // Current mode, readable by the (stable) debounced writer without recreating it.
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  });

  // The searchQuery value this component itself wrote, so the sync-from-URL
  // effect can ignore our own write and not revert a mid-debounce keystroke.
  const lastWrittenRef = useRef<string | null>(null);

  // Write the feed-filter params for a given text + mode. Only touches refs.
  const write = useCallback((text: string, searchMode: SearchMode) => {
    if (!onFeedRef.current) return;
    const trimmed = text.trim();
    if (!trimmed) {
      lastWrittenRef.current = '';
      actionsRef.current.removeAllSearchParams(['searchQuery', 'searchMode']);
      return;
    }
    lastWrittenRef.current = trimmed;
    actionsRef.current.appendMultipleSearchParams([
      { key: 'searchQuery', value: trimmed },
      { key: 'searchMode', value: searchMode },
    ]);
  }, []);

  // Live feed filtering (debounced); reads the current mode via modeRef.
  const debouncedWrite = useMemo(
    () => debounce((text: string) => write(text, modeRef.current), 400),
    [write]
  );

  // Autocomplete suggestions. Held in local state (rather than reading the
  // lazy query's `data`) so we can clear stale cross-mode/cross-query results
  // immediately; a request id ignores out-of-order responses.
  const [triggerSuggest] = useLazyGetSearchAutoCompleteQuery();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionPosts, setSuggestionPosts] = useState<SuggestionPost[]>([]);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  const clearSuggestions = useCallback(() => {
    reqIdRef.current += 1; // invalidate any in-flight response
    setSuggestions([]);
    setSuggestionPosts([]);
    setLoading(false);
  }, []);

  const fetchSuggestions = useCallback(
    (text: string, searchMode: SearchMode) => {
      const trimmed = text.trim();
      if (trimmed.length < MIN_SUGGEST_CHARS) {
        clearSuggestions();
        return;
      }
      reqIdRef.current += 1;
      const reqId = reqIdRef.current;
      // Reveal the dropdown with a loading state once the user pauses typing.
      setOpen(true);
      setLoading(true);
      triggerSuggest({ searchQuery: trimmed, searchMode })
        .unwrap()
        .then((res) => {
          if (reqId !== reqIdRef.current) return;
          setSuggestions(res.suggestions);
          setSuggestionPosts(res.posts);
          setLoading(false);
        })
        .catch(() => {
          if (reqId !== reqIdRef.current) return;
          setSuggestions([]);
          setSuggestionPosts([]);
          setLoading(false);
        });
    },
    [triggerSuggest, clearSuggestions]
  );

  const debouncedSuggest = useMemo(
    () =>
      debounce(
        (text: string, searchMode: SearchMode) =>
          fetchSuggestions(text, searchMode),
        250
      ),
    [fetchSuggestions]
  );

  // Sync the text FROM the URL on EXTERNAL changes only (back button etc.).
  useEffect(() => {
    if (urlValue === lastWrittenRef.current) return; // our own write echoing back
    setInputValue((current) => (current === urlValue ? current : urlValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlValue]);

  // Sync the mode FROM the URL only when one is explicitly present.
  useEffect(() => {
    if (urlSearchMode === 'user' || urlSearchMode === 'title') {
      setMode(urlSearchMode);
    }
  }, [urlSearchMode]);

  // Cancel pending debounced work on unmount.
  useEffect(
    () => () => {
      debouncedWrite.cancel();
      debouncedSuggest.cancel();
    },
    [debouncedWrite, debouncedSuggest]
  );

  // Close the dropdown when clicking outside the component.
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const handleChange = (value: string) => {
    setInputValue(value);
    setHighlight(-1);
    debouncedWrite(value);
    // The dropdown is NOT opened here — it only appears once the debounced
    // suggestion fetch resolves (i.e. after the user stops typing).
    if (value.trim().length < MIN_SUGGEST_CHARS) {
      debouncedSuggest.cancel();
      clearSuggestions();
      setOpen(false);
    } else {
      debouncedSuggest(value, mode);
    }
  };

  const handleModeChange = (nextMode: SearchMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setHighlight(-1);
    debouncedSuggest.cancel();

    const trimmed = inputValue.trim();
    if (trimmed) {
      debouncedWrite.cancel();
      write(inputValue, nextMode);
    }

    if (trimmed.length >= MIN_SUGGEST_CHARS) {
      // Drop stale suggestions but stay in the loading state across the debounce
      // window so the skeleton shows instead of a "No suggestions" flash.
      reqIdRef.current += 1;
      setSuggestions([]);
      setSuggestionPosts([]);
      setLoading(true);
      debouncedSuggest(inputValue, nextMode);
    } else {
      clearSuggestions();
    }
  };

  const selectSuggestion = (value: string) => {
    debouncedWrite.cancel();
    debouncedSuggest.cancel();
    setInputValue(value);
    setOpen(false);
    setHighlight(-1);

    // On the feed, picking a suggestion filters in place — unchanged behaviour.
    if (onFeed) {
      write(value, mode);
      return;
    }
    navigate(
      resolveSearchTarget(value, mode, suggestionPosts, isAuthenticated)
    );
  };

  const submitQuery = () => {
    debouncedWrite.cancel();
    debouncedSuggest.cancel();
    setOpen(false);
    setHighlight(-1);
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const commit = pickCommitLabel(trimmed, suggestions);
    if (commit) {
      selectSuggestion(commit);
      return;
    }
    navigate(feedSearchPath(trimmed, mode));
  };

  const handleClear = () => {
    debouncedWrite.cancel();
    debouncedSuggest.cancel();
    clearSuggestions();
    lastWrittenRef.current = '';
    setInputValue('');
    setOpen(false);
    setHighlight(-1);
    if (onFeed) removeAllSearchParams(['searchQuery', 'searchMode']);
  };

  const hasQuery = inputValue.trim().length >= MIN_SUGGEST_CHARS;
  const showSuggestions = open && hasQuery && suggestions.length > 0;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
      return;
    }
    if (e.key === 'Enter') {
      if (showSuggestions && highlight >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[highlight]);
        return;
      }
      // On the feed the debounce has already filtered; Enter stays inert.
      if (onFeed) return;
      e.preventDefault();
      submitQuery();
      return;
    }
    if (!showSuggestions) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black-400" />
        <Input
          type="text"
          placeholder={MODE_PLACEHOLDER[mode]}
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="pl-9 pr-[6rem] w-full rounded-full"
          aria-label="Search feed"
          role="combobox"
          aria-expanded={open}
          aria-controls="feed-search-dropdown"
        />
        {/* Right cluster: clear button + clickable current-mode badge */}
        <div className="absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              className="flex items-center"
            >
              <X className="h-4 w-4 text-black-400 hover:text-black-600" />
            </button>
          )}
          <Badge
            role="button"
            tabIndex={0}
            aria-label={`Search mode: ${MODE_LABEL[mode]}. Click to switch.`}
            variant="secondary"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
              handleModeChange(mode === 'title' ? 'user' : 'title')
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleModeChange(mode === 'title' ? 'user' : 'title');
              }
            }}
            className="cursor-pointer font-medium"
          >
            {MODE_LABEL[mode]}
          </Badge>
        </div>
      </div>

      {open && (
        <div
          id="feed-search-dropdown"
          className="absolute z-50 mt-2 w-full overflow-hidden rounded-2xl border bg-popover text-popover-foreground shadow-md"
        >
          {/* Mode badges */}
          <div className="flex gap-2 border-b p-2.5">
            {MODES.map((m) => (
              <Badge
                key={m}
                role="button"
                tabIndex={0}
                aria-pressed={mode === m}
                variant={mode === m ? 'default' : 'outline'}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleModeChange(m)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleModeChange(m);
                  }
                }}
                className="cursor-pointer"
              >
                {MODE_LABEL[m]}
              </Badge>
            ))}
          </div>

          {/* Suggestions */}
          {loading && (
            <div className="space-y-2 p-3" aria-busy="true">
              {['w-3/4', 'w-full', 'w-2/3', 'w-5/6'].map((w) => (
                <Skeleton key={w} className={`h-5 ${w}`} />
              ))}
            </div>
          )}
          {!loading && showSuggestions && (
            <ul className="max-h-64 overflow-auto py-1" role="listbox">
              {suggestions.map((s, i) => (
                <li key={s}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === highlight}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => selectSuggestion(s)}
                    className={`block w-full truncate px-4 py-2 text-left text-sm ${
                      i === highlight ? 'bg-accent' : 'hover:bg-accent'
                    }`}
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!loading && !showSuggestions && hasQuery && (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No suggestions
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FeedSearchInput;
